import { useMemo, useState } from "react";
import {
  BadgeCheck,
  FlaskConical,
  Gift,
  Search,
  ShieldAlert,
  Sparkles,
} from "lucide-react";
import { Button, Skeleton } from "@/components/ui";
import { Chip, StatusBanner } from "@/components/marketplace";
import { Select } from "@/components/Select";
import { useTranslation } from "@/hooks/useTranslation";
import {
  useCreatorSearch,
  useDiscoveryProviderName,
  useParseCreatorSearch,
  type CreatorSearchInput,
  type SocialPlatform,
} from "@/hooks/useMarketplace";
import { useAuth } from "@/contexts/AuthContext";
import { AISearchBar } from "./AISearchBar";
import { CreatorCard } from "./CreatorCard";
import {
  COUNTRIES,
  COUNTRY_LABELS,
  FOLLOWER_BANDS,
  NICHES,
  PLATFORMS,
  PLATFORM_LABELS,
} from "./constants";

/**
 * A grid of portrait-shaped skeletons.
 *
 * Skeletons, never spinners: a centred spinner tells you something is happening
 * and nothing about what. This one has the aspect ratio of the cards that are
 * coming, so the page does not jump when they arrive.
 */
function CreatorGridSkeleton({ count = 6 }: { count?: number }) {
  return (
    <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
      {Array.from({ length: count }).map((_, i) => (
        <div
          key={i}
          className="flex aspect-4/5 flex-col justify-end overflow-hidden rounded-card bg-neutral-100 p-4"
        >
          {/* Widths vary so a stack of these never reads as a grid. */}
          <Skeleton className="h-4 w-[62%] rounded-sm" />
          <Skeleton className="mt-2 h-3 w-[44%] rounded-sm" />
          <div className="mt-4 flex gap-1.5">
            <Skeleton className="h-4 w-14 rounded-pill" />
            <Skeleton className="h-4 w-20 rounded-pill" />
          </div>
          <Skeleton className="mt-4 h-3 w-[80%] rounded-sm" />
          <Skeleton className="mt-2 h-3 w-[68%] rounded-sm" />
          <Skeleton className="mt-4 h-6 w-24 rounded-sm" />
        </div>
      ))}
    </div>
  );
}

export default function CreatorDirectory() {
  const { t } = useTranslation();
  const { user } = useAuth();

  // Operator-facing. Per-profile badges already tell a brand which rows are
  // invented; this tells whoever runs the trial *why*, before they demo the
  // catalogue to someone who knows these creators personally.
  const isAdmin = user?.platformRole === "ADMIN";
  const { providerName } = useDiscoveryProviderName(isAdmin);
  const usingFixtures = isAdmin && providerName === "fixture";

  const [search, setSearch] = useState("");
  const [countries, setCountries] = useState<string[]>([]);
  const [niches, setNiches] = useState<string[]>([]);
  const [platforms, setPlatforms] = useState<SocialPlatform[]>([]);
  const [minFollowers, setMinFollowers] = useState("");
  const [maxRate, setMaxRate] = useState("");
  const [acceptsGifting, setAcceptsGifting] = useState(false);
  const [verifiedOnly, setVerifiedOnly] = useState(false);
  const [showAllFilters, setShowAllFilters] = useState(false);

  const { parse, loading: parsing } = useParseCreatorSearch();
  const [interpretation, setInterpretation] = useState<string | null>(null);
  const [unmapped, setUnmapped] = useState<string[]>([]);
  const [parseError, setParseError] = useState<string | null>(null);

  // Omit empty filters entirely rather than sending nulls — the resolver treats
  // an absent key as "no constraint", and an explicit empty array is not that.
  const input = useMemo<CreatorSearchInput>(() => {
    const next: CreatorSearchInput = {};
    if (search.trim()) next.query = search.trim();
    if (countries.length) next.countries = countries;
    if (niches.length) next.niches = niches;
    if (platforms.length) next.platforms = platforms;
    if (minFollowers) next.minFollowers = Number(minFollowers);
    if (maxRate) next.maxRate = Number(maxRate);
    if (acceptsGifting) next.acceptsGifting = true;
    if (verifiedOnly) next.verifiedOnly = true;
    return next;
  }, [search, countries, niches, platforms, minFollowers, maxRate, acceptsGifting, verifiedOnly]);

  const { creators, totalCount, loading, hasNextPage, loadMore } = useCreatorSearch(input);

  const activeFilterCount =
    countries.length +
    niches.length +
    platforms.length +
    (minFollowers ? 1 : 0) +
    (maxRate ? 1 : 0) +
    (acceptsGifting ? 1 : 0) +
    (verifiedOnly ? 1 : 0);

  const toggle = <T,>(list: T[], set: (v: T[]) => void, value: T) =>
    set(list.includes(value) ? list.filter((v) => v !== value) : [...list, value]);

  const clearAll = () => {
    setCountries([]);
    setNiches([]);
    setPlatforms([]);
    setMinFollowers("");
    setMaxRate("");
    setAcceptsGifting(false);
    setVerifiedOnly(false);
    setInterpretation(null);
    setUnmapped([]);
  };

  /**
   * Write the AI's reading of the sentence into the ordinary filter state.
   *
   * Everything downstream is unchanged — the same `useCreatorSearch` runs the
   * same query. The AI only decides where the controls start, and the chips are
   * expanded so the user sees that and can correct it. Fields the sentence did
   * not mention are reset rather than left over from a previous search, or the
   * results would silently answer a mix of two questions.
   */
  const runAiSearch = async (text: string) => {
    setParseError(null);
    try {
      const result = await parse(text);
      const f = result.filters;

      setCountries(f.countries ?? []);
      setNiches(f.niches ?? []);
      setPlatforms(f.platforms ?? []);
      setMinFollowers(f.minFollowers ? String(f.minFollowers) : "");
      setMaxRate(f.maxRate ? String(f.maxRate) : "");
      setAcceptsGifting(f.acceptsGifting ?? false);
      setVerifiedOnly(f.verifiedOnly ?? false);
      setSearch(f.query ?? "");

      setInterpretation(result.interpretation);
      setUnmapped(result.unmapped);
      setShowAllFilters(true);
    } catch (e) {
      setParseError(e instanceof Error ? e.message : String(e));
    }
  };

  /**
   * Applied filters are always on screen, as chips with an × on each.
   *
   * They used to live behind a "Filtros" button. A filter you cannot see is one
   * you forget you set, and the usual result was a brand concluding there were
   * no creators in Colombia while a niche filter from ten minutes ago was still
   * running.
   */
  const appliedChips = [
    ...countries.map((c) => ({
      key: `country:${c}`,
      label: COUNTRY_LABELS[c],
      remove: () => toggle(countries, setCountries, c),
    })),
    ...niches.map((n) => ({
      key: `niche:${n}`,
      label: t(`marketplace.niches.${n}`, { defaultValue: n }),
      remove: () => toggle(niches, setNiches, n),
    })),
    ...platforms.map((p) => ({
      key: `platform:${p}`,
      label: PLATFORM_LABELS[p],
      remove: () => toggle(platforms, setPlatforms, p),
    })),
  ];

  return (
    <div className="adv-page">
      {usingFixtures && (
        <StatusBanner
          tone="danger"
          className="mb-5"
          icon={<FlaskConical className="size-[15px]" strokeWidth={2.2} />}
          title={t("marketplace.discover.fixtureBannerTitle")}
        >
          {t("marketplace.discover.fixtureBannerBody")}
        </StatusBanner>
      )}

      <header className="mb-7 max-w-2xl">
        <h1 className="text-page-title text-balance text-neutral-900">
          {t("marketplace.discover.title")}
        </h1>
        <p className="mt-3 text-lead text-neutral-500">
          {t("marketplace.discover.subtitle")}
        </p>
      </header>

      <AISearchBar onSearch={runAiSearch} loading={parsing} />

      {parseError && (
        <StatusBanner tone="danger" className="mt-4">
          {parseError}
        </StatusBanner>
      )}

      {/* What the AI understood, stated plainly so a wrong reading is obvious
          before the user draws conclusions from the results. */}
      {interpretation && (
        <StatusBanner
          tone="info"
          className="mt-4"
          icon={<Sparkles className="size-[15px]" strokeWidth={2.2} />}
          title={interpretation}
        >
          {t("marketplace.aiSearch.editHint")}
        </StatusBanner>
      )}

      {/* Criteria with no column behind them. Saying so beats returning a list
          that looks like a complete answer to a question it only half covers. */}
      {unmapped.length > 0 && (
        <StatusBanner
          tone="warning"
          className="mt-3"
          icon={<ShieldAlert className="size-[15px]" strokeWidth={2.2} />}
        >
          {t("marketplace.aiSearch.unmapped")} <strong>{unmapped.join(", ")}</strong>
        </StatusBanner>
      )}

      <div className="relative mt-5">
        <Search className="pointer-events-none absolute left-3.5 top-1/2 size-4 -translate-y-1/2 text-neutral-400" />
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t("marketplace.discover.searchPlaceholder")}
          className="w-full rounded-control border border-neutral-200 bg-white py-2.5 pl-10 pr-3 text-control text-neutral-900 transition-colors placeholder:text-neutral-400 hover:border-neutral-300"
        />
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-2">
        <span className="adv-label mr-1">{t("marketplace.filters.eyebrow")}</span>

        {appliedChips.map((chip) => (
          <Chip
            key={chip.key}
            variant="active"
            onRemove={chip.remove}
            removeLabel={t("marketplace.filters.clear")}
          >
            {chip.label}
          </Chip>
        ))}

        <Chip
          variant={acceptsGifting ? "active" : "default"}
          icon={<Gift className="size-3" strokeWidth={2.4} />}
          onClick={() => setAcceptsGifting((v) => !v)}
        >
          {t("marketplace.filters.gifting")}
        </Chip>

        <Chip
          variant={verifiedOnly ? "active" : "accent"}
          icon={<BadgeCheck className="size-3" strokeWidth={2.4} />}
          onClick={() => setVerifiedOnly((v) => !v)}
        >
          {t("marketplace.filters.verifiedOnly")}
        </Chip>

        <Chip variant="dashed" onClick={() => setShowAllFilters((v) => !v)}>
          {showAllFilters ? "−" : "+"} {t("marketplace.filters.title")}
        </Chip>

        {activeFilterCount > 0 && (
          <button
            type="button"
            onClick={clearAll}
            className="ml-auto text-meta font-medium text-neutral-500 transition-colors hover:text-neutral-900"
          >
            {t("marketplace.filters.clear")}
          </button>
        )}
      </div>

      {showAllFilters && (
        <div className="mt-4 space-y-5 rounded-card border border-neutral-200 bg-white px-5 py-[18px]">
          <div>
            <p className="adv-label mb-2">{t("marketplace.filters.country")}</p>
            <div className="flex flex-wrap gap-2">
              {COUNTRIES.map((c) => (
                <Chip
                  key={c}
                  variant={countries.includes(c) ? "active" : "default"}
                  onClick={() => toggle(countries, setCountries, c)}
                >
                  {COUNTRY_LABELS[c]}
                </Chip>
              ))}
            </div>
          </div>

          <div>
            <p className="adv-label mb-2">{t("marketplace.filters.niche")}</p>
            <div className="flex flex-wrap gap-2">
              {NICHES.map((n) => (
                <Chip
                  key={n}
                  variant={niches.includes(n) ? "active" : "default"}
                  onClick={() => toggle(niches, setNiches, n)}
                >
                  {t(`marketplace.niches.${n}`, { defaultValue: n })}
                </Chip>
              ))}
            </div>
          </div>

          <div className="grid gap-5 sm:grid-cols-3">
            <div>
              <p className="adv-label mb-2">{t("marketplace.filters.platform")}</p>
              <div className="flex flex-wrap gap-2">
                {PLATFORMS.map((p) => (
                  <Chip
                    key={p}
                    variant={platforms.includes(p) ? "active" : "default"}
                    onClick={() => toggle(platforms, setPlatforms, p)}
                  >
                    {PLATFORM_LABELS[p]}
                  </Chip>
                ))}
              </div>
            </div>

            <div>
              <p className="adv-label mb-2">{t("marketplace.filters.reach")}</p>
              <Select
                value={minFollowers}
                onChange={setMinFollowers}
                options={FOLLOWER_BANDS.map((b) => ({ value: b.value, label: t(b.labelKey) }))}
                size="md"
              />
            </div>

            <div>
              <p className="adv-label mb-2">{t("marketplace.filters.maxRate")}</p>
              <input
                type="number"
                min="0"
                value={maxRate}
                onChange={(e) => setMaxRate(e.target.value)}
                placeholder={t("marketplace.filters.maxRatePlaceholder")}
                className="w-full rounded-control border border-neutral-200 bg-white px-3 py-2.5 text-control text-neutral-900 transition-colors placeholder:text-neutral-400 hover:border-neutral-300"
              />
            </div>
          </div>
        </div>
      )}

      <div className="mt-7">
        {loading && creators.length === 0 ? (
          <CreatorGridSkeleton />
        ) : creators.length === 0 ? (
          <div className="rounded-card border border-neutral-200 bg-white px-5 py-16 text-center">
            <p className="text-card-title-sm text-neutral-900">
              {t("marketplace.discover.emptyTitle")}
            </p>
            {/* After an AI search, zero results reads as "the AI failed" when
                the parse was usually fine and the supply simply isn't there.
                Say which it is, and point at the filters rather than the
                sentence. */}
            <p className="mx-auto mt-2 max-w-md text-meta text-neutral-500">
              {interpretation
                ? t("marketplace.aiSearch.emptyBody")
                : t("marketplace.discover.emptyBody")}
            </p>
            {activeFilterCount > 0 && (
              <Button variant="secondary" className="mt-5" onClick={clearAll}>
                {t("marketplace.filters.clear")}
              </Button>
            )}
          </div>
        ) : (
          <>
            <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {creators.map((c, i) => (
                <CreatorCard key={c.id} creator={c} index={i} />
              ))}
            </div>

            {/* Height is defined by the content — no fixed min-height under
                the grid, or a short result set leaves a hole. */}
            <div className="mt-7 flex flex-col items-center gap-2">
              {hasNextPage && (
                <Button variant="secondary" onClick={() => loadMore()} disabled={loading}>
                  {loading ? t("common.loading") : t("marketplace.discover.loadMore")}
                </Button>
              )}
              <p className="text-fine text-neutral-400">
                {t("marketplace.discover.showing", {
                  shown: creators.length,
                  total: totalCount,
                })}
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
